// Akce vpravo v hlavičce okna (řádek 2).
//   - Native okna: frameworkové tlačítko „Přenačíst" (reload obsahu agendy) — společné pro všechny
//     native agendy, dodává ho framework (nikoli agenda). Přenačtení viz `@/shared/lib/layerReload`.
//   - Legacy okna: well-known akce z „vycucnutého" chrome → samostatná tlačítka s Phoenix ikonou
//     (reload legacy okno reportuje samo) + kebab „Další akce" = toggle „Vizualizace zámků" + neznámé.
// Klik na legacy akci vyšle `window/action` (chrome akce) resp. `window/toggle-locks` (zámek) do iframu.

import { useLayerReloadStore } from '@/shared/lib/layerReload';
import { DropdownMenu, MenuDivider, MenuItem } from '@/shared/ui/dropdown-menu';
import { Icon } from '@/shared/ui/icon';

import type { DesktopWindow } from '../core/deriveDesktopProjection';
import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';
import { useLockVizStore } from '../legacy/lockVizStore';
import { sendWindowAction } from '../legacy/sendWindowAction';
import { sendWindowToggleLocks } from '../legacy/sendWindowToggleLocks';
import { wellKnownIcon } from '../legacy/wellKnownActions';

export function WindowActions({ win }: { win: DesktopWindow }) {
  const chrome = useLegacyWindowChromeStore((s) => s.byLayer[win.layerId]);
  const lockOn = useLockVizStore((s) => s.byLayer[win.layerId] ?? false);
  const setLockEnabled = useLockVizStore((s) => s.setEnabled);
  const requestReload = useLayerReloadStore((s) => s.requestReload);

  // Native agendy: framework přidává jen tlačítko „Přenačíst" (zámek + kebab jsou legacy-only).
  if (win.kind !== 'legacy') {
    return (
      <span className="pwm-actions" onPointerDown={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="pwm-act-btn"
          title="Přenačíst"
          aria-label="Přenačíst"
          onClick={() => requestReload(win.layerId)}
        >
          <Icon name="refresh" size={14} />
        </button>
      </span>
    );
  }

  const actions = chrome?.actions ?? [];
  const known = actions.filter((a) => wellKnownIcon(a.key) !== undefined);
  const unknown = actions.filter((a) => wellKnownIcon(a.key) === undefined);

  const toggleLocks = () => {
    const next = !lockOn;
    setLockEnabled(win.layerId, next);
    sendWindowToggleLocks(win.layerId, next);
  };

  return (
    <span className="pwm-actions" onPointerDown={(e) => e.stopPropagation()}>
      {known.map((a) => {
        const icon = wellKnownIcon(a.key);
        if (!icon) return null;
        return (
          <button
            key={a.id}
            type="button"
            className="pwm-act-btn"
            title={a.label}
            disabled={a.enabled === false}
            data-on={a.toggled || undefined}
            onClick={() => sendWindowAction(win.layerId, a.id)}
          >
            <Icon name={icon} size={14} />
          </button>
        );
      })}

      {known.length > 0 && <span className="pwm-act-divider" />}

      <DropdownMenu
        align="end"
        backdrop
        trigger={
          <button
            type="button"
            className="pwm-act-btn"
            title="Další akce"
            data-on={lockOn || undefined}
          >
            <Icon name="more" size={13} />
          </button>
        }
      >
        <MenuItem active={lockOn} icon={lockOn ? 'unlock' : 'lock'} onClick={toggleLocks}>
          Vizualizace zámků
        </MenuItem>
        {unknown.length > 0 && <MenuDivider />}
        {unknown.map((a) => (
          <MenuItem
            key={a.id}
            disabled={a.enabled === false}
            onClick={() => sendWindowAction(win.layerId, a.id)}
          >
            {a.label || 'Akce'}
          </MenuItem>
        ))}
      </DropdownMenu>
    </span>
  );
}
